import { useState, type FormEvent } from 'react';

import {
  parseLanguages,
  setupSite,
  SITE_FEATURES,
  type SiteFeature,
} from '@/features/site-setup/siteSetup';
import type { SiteBody, TenantBody } from '@/shared/api/generated/model';
import { sitesCreate, sitesUpdate } from '@/shared/api/generated/sites/sites';
import { ApiError } from '@/shared/api/http';
import { slugify } from '@/shared/lib/slugify';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Select, SelectItem } from '@/shared/ui/Select';
import { toast } from '@/shared/ui/Toast';

import styles from './SiteFormDialog.module.scss';

export interface SiteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Заполнено — правим существующий, пусто — заводим новый. */
  site?: SiteBody | undefined;
  tenants: TenantBody[];
  /** Какой тенант подставить в новом сайте. Пусто — выбирает человек. */
  defaultTenantID?: string;
  onDone: () => void;
}

/**
 * Форма сайта: одна на создание и на переименование. Новый сайт — в два шага:
 * название, затем что будет на сайте, языки и адреса (ресерч 13).
 */
export function SiteFormDialog({
  open,
  onOpenChange,
  site,
  tenants,
  defaultTenantID = '',
  onDone,
}: SiteFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <Body
          site={site}
          tenants={tenants}
          defaultTenantID={defaultTenantID}
          onOpenChange={onOpenChange}
          onDone={onDone}
        />
      )}
    </Dialog>
  );
}

function Body({
  site,
  tenants,
  defaultTenantID,
  onOpenChange,
  onDone,
}: Omit<SiteFormDialogProps, 'open' | 'defaultTenantID'> & { defaultTenantID: string }) {
  const editing = site !== undefined;

  const [tenantID, setTenantID] = useState(site?.tenant_id ?? defaultTenantID);
  const [name, setName] = useState(site?.name ?? '');
  const [slug, setSlug] = useState(site?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(editing);

  // Второй шаг нового сайта.
  const [step, setStep] = useState<'name' | 'setup'>('name');
  const [features, setFeatures] = useState<SiteFeature[]>([]);
  const [languages, setLanguages] = useState('ru');
  const [developmentURL, setDevelopmentURL] = useState('');
  const [productionURL, setProductionURL] = useState('');

  const [error, setError] = useState<string>();
  const [slugError, setSlugError] = useState<string>();
  const [tenantError, setTenantError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const fail = (cause: unknown) => {
    // 409 означает занятое короткое имя — это ошибка поля, и показывать её
    // надо у него, а не общим сообщением внизу окна.
    if (cause instanceof ApiError && cause.status === 409) {
      setSlugError(cause.detail);
      setStep('name');
    } else if (cause instanceof ApiError) {
      setError(cause.detail);
      setStep('name');
    } else {
      setError('Не удалось сохранить. Попробуйте ещё раз.');
    }
  };

  const create = async () => {
    const response = await sitesCreate({ tenant_id: tenantID, name: name.trim(), slug });

    if (response.status !== 201) {
      throw new Error('Неожиданный ответ на создание сайта');
    }

    // Сайт уже есть: недоделанное ниже поправят в настройках, а не создадут
    // второй сайт повторным нажатием.
    const failed = await setupSite(response.data.id, response.data.etag, {
      features,
      languages: parseLanguages(languages),
      developmentURL,
      productionURL,
    });

    if (failed.length === 0) {
      toast.success('Сайт создан');
    } else {
      toast.error(
        `Сайт создан, но не сохранилось: ${failed.join('; ')}. Поправьте в настройках сайта.`,
      );
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setSlugError(undefined);
    setTenantError(undefined);

    if (!editing && tenantID === '') {
      setTenantError('Выберите тенанта');

      return;
    }

    if (!editing && step === 'name') {
      setStep('setup');

      return;
    }

    setBusy(true);

    const request = editing
      ? sitesUpdate(site.id, { name: name.trim(), slug }, { 'If-Match': site.etag }).then(() => {
          toast.success('Изменения сохранены');
        })
      : create();

    request
      .then(() => {
        onDone();
        onOpenChange(false);
      })
      .catch(fail)
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <DialogContent
      title={editing ? 'Переименовать сайт' : 'Новый сайт'}
      description={
        editing
          ? undefined
          : step === 'name'
            ? 'Вместе с сайтом заведутся окружения Production и Development.'
            : 'Всё можно поменять потом в настройках сайта.'
      }
      size={step === 'setup' ? 'md' : 'sm'}
      dismissible={false}
      footer={
        <>
          <Button
            variant="outline"
            tone="neutral"
            type="button"
            onClick={() => {
              if (step === 'setup') {
                setStep('name');
              } else {
                onOpenChange(false);
              }
            }}
          >
            {step === 'setup' ? 'Назад' : 'Отмена'}
          </Button>
          <Button type="submit" form="site-form" loading={busy}>
            {editing ? 'Сохранить' : step === 'name' ? 'Дальше' : 'Создать'}
          </Button>
        </>
      }
    >
      <form id="site-form" className={styles['form']} onSubmit={handleSubmit} noValidate>
        {step === 'setup' ? (
          <>
            <fieldset className={styles['features']}>
              <legend className={styles['legend']}>Что будет на сайте</legend>
              {SITE_FEATURES.map((feature) => (
                <div key={feature.key} className={styles['feature']}>
                  <Checkbox
                    checked={features.includes(feature.key)}
                    onCheckedChange={(checked) => {
                      setFeatures(
                        checked === true
                          ? SITE_FEATURES.map((f) => f.key).filter(
                              (key) => key === feature.key || features.includes(key),
                            )
                          : features.filter((key) => key !== feature.key),
                      );
                    }}
                  >
                    <span className={styles['feature__title']}>{feature.title}</span>
                    <span className={styles['feature__text']}>{feature.text}</span>
                  </Checkbox>
                </div>
              ))}
            </fieldset>

            <Field label="Языки" hint="Через запятую, первый — основной: ru, en, kk.">
              <Input
                mono
                value={languages}
                onChange={(event) => {
                  setLanguages(event.target.value);
                }}
              />
            </Field>

            <div className={styles['row']}>
              <Field label="Адрес Development" hint="Тестовый стенд сайта — для предпросмотра.">
                <Input
                  mono
                  value={developmentURL}
                  placeholder="https://dev.example.ru"
                  onChange={(event) => {
                    setDevelopmentURL(event.target.value);
                  }}
                />
              </Field>
              <Field label="Адрес Production" hint="Боевой сайт. Можно указать потом.">
                <Input
                  mono
                  value={productionURL}
                  placeholder="https://example.ru"
                  onChange={(event) => {
                    setProductionURL(event.target.value);
                  }}
                />
              </Field>
            </div>
          </>
        ) : (
          nameFields()
        )}
      </form>
    </DialogContent>
  );

  // Обычная функция, не компонент: компонент, объявленный в рендере, React
  // пересоздавал бы на каждое нажатие — поле теряло бы фокус.
  function nameFields() {
    return (
      <>
        {!editing && (
          <Field label="Тенант" required error={tenantError}>
            <Select value={tenantID} onValueChange={setTenantID} placeholder="Выберите тенанта">
              {tenants.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id} description={tenant.slug}>
                  {tenant.name}
                </SelectItem>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Название" required error={error}>
          <Input
            value={name}
            autoFocus
            placeholder="Портал"
            onChange={(event) => {
              setName(event.target.value);

              if (!slugTouched) {
                setSlug(slugify(event.target.value));
              }
            }}
          />
        </Field>

        <Field
          label="Короткое имя"
          required
          error={slugError}
          hint="Уникально внутри тенанта. Строчные латинские буквы, цифры и дефис."
        >
          <Input
            mono
            value={slug}
            placeholder="portal"
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value);
            }}
          />
        </Field>
      </>
    );
  }
}
